import { FC, useState, useRef, useCallback } from 'react';
import { ArrowRight, MessageSquare, Zap } from 'lucide-react';
import { Button, Typography, cn } from '@crisellevaldez/speculo-ui';
import { useUser } from '../../hooks/useUser';
import { useAskRemiStore } from '../../stores/useAskRemiStore';

const quickActions = ['Show my leads', "Today's calls", 'Draft follow-ups', 'Weekly insights'];

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/** Attaches mouse-tracking CSS vars for the global .mouse-glow class */
function useMouseGlow<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  const onMouseMove = useCallback((e: React.MouseEvent<T>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
    el.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
  }, []);

  return { ref, onMouseMove };
}

/** Quick action card — uses global .mouse-glow CSS utility */
const QuickActionCard: FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => {
  const { ref, onMouseMove } = useMouseGlow<HTMLButtonElement>();

  return (
    <button
      ref={ref}
      onClick={onClick}
      onMouseMove={onMouseMove}
      className="mouse-glow group p-2.5 sm:p-3.5 rounded-lg border border-foreground/30 card-surface shadow-sm text-left text-xs sm:text-sm font-medium text-foreground leading-snug transition-all duration-200 hover:shadow-md flex items-center justify-between gap-2"
    >
      <span className="relative z-10">{label}</span>
      <ArrowRight className="relative z-10 w-3.5 h-3.5 text-foreground opacity-0 -translate-x-1 group-hover:opacity-60 group-hover:translate-x-0 transition-all duration-200 shrink-0" />
    </button>
  );
};

/** Single-line input bar — extracted to top level so React doesn't remount on every keystroke */
const InputBar: FC<{
  inputRef: React.RefObject<HTMLInputElement>;
  value: string;
  onChange: (val: string) => void;
  onSend: () => void;
}> = ({ inputRef, value, onChange, onSend }) => (
  <div className="relative flex items-center gap-2 sm:gap-3 border border-foreground/30 rounded-lg shadow-md overflow-hidden transition-all duration-300 focus-within:border-amber-500/80 focus-within:shadow-[0_0_16px_rgba(217,155,20,0.18)] card-surface px-3 py-2.5 sm:px-4 sm:py-3">
    <div className="btn-ai-gradient rounded-lg p-1.5 shrink-0">
      <Zap className="h-4 w-4 text-white dark:text-zinc-900" />
    </div>
    <div className="relative flex-1 min-w-0">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSend();
          }
        }}
        className="w-full bg-transparent text-foreground text-sm outline-none"
      />
      {!value && (
        <span className="pointer-events-none absolute inset-0 flex items-center text-sm text-muted-foreground select-none">
          Ask Remi anything
          <span className="animate-dot-pulse ml-1 mr-0.5 text-lg font-bold leading-none" style={{ animationDelay: '0ms' }}>.</span>
          <span className="animate-dot-pulse mr-0.5 text-lg font-bold leading-none" style={{ animationDelay: '220ms' }}>.</span>
          <span className="animate-dot-pulse text-lg font-bold leading-none" style={{ animationDelay: '440ms' }}>.</span>
        </span>
      )}
    </div>
    <Button
      variant="ghost"
      size="icon"
      onClick={onSend}
      disabled={!value.trim()}
      className={cn(
        'w-8 h-8 rounded-lg transition-all shrink-0',
        value.trim()
          ? 'bg-foreground text-background hover:bg-foreground/90'
          : 'bg-muted text-muted-foreground'
      )}
    >
      <ArrowRight className="w-4 h-4" />
    </Button>
  </div>
);

export const AskRemiHero: FC = () => {
  const { user } = useUser();
  const [input, setInput] = useState('');
  const [waveKey, setWaveKey] = useState(0);
  const [dragRotation, setDragRotation] = useState<number | null>(null);
  const [reverting, setReverting] = useState(false);
  const waveRef = useRef<HTMLSpanElement>(null);
  const revertTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragState = useRef<{
    cx: number;
    cy: number;
    startAngle: number;
    baseRotation: number;
    moved: boolean;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleWavePointerDown = useCallback(
    (e: React.PointerEvent<HTMLSpanElement>) => {
      const el = waveRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const startAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
      dragState.current = {
        cx,
        cy,
        startAngle,
        baseRotation: dragRotation ?? 0,
        moved: false,
      };
      el.setPointerCapture(e.pointerId);
    },
    [dragRotation]
  );

  const handleWavePointerMove = useCallback((e: React.PointerEvent<HTMLSpanElement>) => {
    const s = dragState.current;
    if (!s) return;
    const angle = Math.atan2(e.clientY - s.cy, e.clientX - s.cx) * (180 / Math.PI);
    const delta = angle - s.startAngle;
    if (Math.abs(delta) > 2) s.moved = true;
    setDragRotation(s.baseRotation + delta);
  }, []);

  const handleWavePointerUp = useCallback((e: React.PointerEvent<HTMLSpanElement>) => {
    const s = dragState.current;
    if (!s) return;
    const moved = s.moved;
    dragState.current = null;
    waveRef.current?.releasePointerCapture(e.pointerId);
    if (!moved) {
      setDragRotation(null);
      setWaveKey((k) => k + 1);
      return;
    }
    // Drop after drag → animate back to rest
    if (revertTimer.current) clearTimeout(revertTimer.current);
    setReverting(true);
    setDragRotation(0);
    revertTimer.current = setTimeout(() => {
      setReverting(false);
      setDragRotation(null);
    }, 350);
  }, []);

  const { messages, sendMessage, openModal } = useAskRemiStore();
  const hasConversation = messages.length > 0;
  const firstName = user?.firstName || 'there';

  /** Strip markdown syntax so the preview reads as plain text */
  const stripMarkdown = (text: string): string =>
    text
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/\[(.+?)\]\(.+?\)/g, '$1')
      .replace(/^#+\s+/gm, '')
      .replace(/^[*-]\s+/gm, '• ');

  const handleSend = useCallback(
    (text?: string) => {
      const message = text || input.trim();
      if (!message) return;

      setInput('');
      openModal();
      sendMessage(message);
    },
    [input, sendMessage, openModal]
  );

  return (
    <div className="max-w-3xl mx-auto ask-remi-gradient px-3 sm:px-4 lg:px-0">
      <div className="pt-4 sm:pt-6 lg:pt-10 2xl:pt-20 pb-4 sm:pb-5 2xl:pb-8">
        <div className="text-center mb-4 sm:mb-6 2xl:mb-8">
          <Typography
            variant="h1"
            as="h1"
            className="!text-xl sm:!text-2xl md:!text-3xl 2xl:!text-4xl !font-bold tracking-tight"
          >
            {getGreeting()}, {firstName}{' '}
            <span
              ref={waveRef}
              key={waveKey}
              role="button"
              aria-label="Wave"
              onPointerDown={handleWavePointerDown}
              onPointerMove={handleWavePointerMove}
              onPointerUp={handleWavePointerUp}
              onPointerCancel={handleWavePointerUp}
              className={cn(
                'inline-block select-none touch-none',
                dragState.current ? 'cursor-grabbing' : 'cursor-grab',
                reverting && 'transition-transform duration-300 ease-out',
                dragRotation === null && !reverting && 'animate-wave-once'
              )}
              style={{
                transformOrigin: '50% 50%',
                transform: dragRotation !== null ? `rotate(${dragRotation}deg)` : undefined,
              }}
            >
              👋
            </span>
          </Typography>
          <Typography variant="muted" as="p" className="mt-1.5 2xl:mt-2 !text-sm 2xl:!text-base text-foreground">
            What can I help you with?
          </Typography>
        </div>

        {/* Input */}
        <div className="mb-4">
          <InputBar
            inputRef={inputRef}
            value={input}
            onChange={setInput}
            onSend={() => handleSend()}
          />
        </div>

        {/* Resume conversation banner */}
        {hasConversation && (
          <button
            onClick={openModal}
            className="w-full mb-4 flex items-center gap-2 sm:gap-3 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border border-amber-500/30 card-surface text-left transition-all hover:border-amber-500/60 hover:shadow-md"
          >
            <div className="btn-ai-gradient rounded-lg p-2 shrink-0">
              <MessageSquare className="w-4 h-4 text-white dark:text-zinc-900" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Resume conversation</p>
              <p className="text-xs text-muted-foreground truncate">
                {stripMarkdown(messages[messages.length - 1].content)}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
          </button>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          {quickActions.map((label) => (
            <QuickActionCard key={label} label={label} onClick={() => handleSend(label)} />
          ))}
        </div>
      </div>
    </div>
  );
};
